import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  Employee,
  EmployeeListApiProps,
  EmployeeListSliceState,
  EmploymentStatus,
  updateHierarchyReportingManagerType,
} from '../../../types/EmployeeDirectory/EmployeesList/employeeListTypes'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import employeeListApi from '../../../middleware/api/EmployeeDirectory/EmployeesList/employeeListApi'

const getEmployees = createAsyncThunk(
  'category/getEmployees',
  async (props: EmployeeListApiProps, thunkApi) => {
    try {
      return await employeeListApi.getEmployees(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const updateReportee = createAsyncThunk(
  'employeeList/updateReportee',
  async (
    {
      employee,
      newmanager,
      oldmanager,
    }: {
      employee: number | string | undefined
      newmanager: number
      oldmanager: number
    },
    thunkApi,
  ) => {
    try {
      return await employeeListApi.updateReportee({
        employee,
        newmanager,
        oldmanager,
      })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const curStatus = 'Active'

const initialEmployeeListState: EmployeeListSliceState = {
  employees: [],
  selectedEmploymentStatus: EmploymentStatus.active,
  listSize: 0,
  isLoading: ApiLoadingState.idle,
  isUpdated: false,
  selectStatusValue: curStatus,
  updateHierarchyReportingManagerType: {
    employee: 0,
    newmanager: 0,
    oldmanager: 0,
  },
  SelectCustom: 'Select Date',
  FromDateFilter: '',
  ToDateFilter: '',
  country: '',
  searchInput: '',
}

const employeeListSlice = createSlice({
  name: 'employeeList',
  initialState: initialEmployeeListState,
  reducers: {
    setStatusValue: (state, action) => {
      state.selectStatusValue = action.payload
    },
    clearSelectStatus: (state) => {
      state.selectStatusValue = 'Active'
    },
    clearEmployeeList: (state) => {
      state.employees = []
    },
    setIsUpdated: (state, action) => {
      state.isUpdated = action.payload
    },
    // changeSelectedEmploymentStatus: (state, action) => {
    //   state.selectedEmploymentStatus = action.payload as EmploymentStatus
    // },
    setSelectCustom: (state, action) => {
      state.SelectCustom = action.payload as string
    },
    setFromDateFilter: (state, action) => {
      state.FromDateFilter = action.payload as string
    },
    setToDateFilter: (state, action) => {
      state.ToDateFilter = action.payload as string
    },
    setSelectedCountry: (state, action) => {
      state.country = action.payload as string
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload as string
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(getEmployees.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })

      .addMatcher(isAnyOf(getEmployees.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employees = action.payload.emps as Employee[]
        state.listSize = action.payload.Empsize
      })
      .addMatcher(isAnyOf(updateReportee.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.updateHierarchyReportingManagerType =
          action.payload as unknown as updateHierarchyReportingManagerType
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.employeeList.isLoading
const employees = (state: RootState): Employee[] => state.employeeList.employees
const listSize = (state: RootState): number => state.employeeList.listSize
const selectedEmploymentStatus = (state: RootState): EmploymentStatus =>
  state.employeeList.selectedEmploymentStatus
const getSelectedStatusValue = (state: RootState): string =>
  state.employeeList.selectStatusValue
const isUpdated = (state: RootState): boolean => state.employeeList.isUpdated
const SelectCustom = (state: RootState): string =>
  state.employeeList.SelectCustom
const FromDateFilter = (state: RootState): string | Date =>
  state.employeeList.FromDateFilter
const ToDateFilter = (state: RootState): string | Date =>
  state.employeeList.ToDateFilter
const selectedCountry = (state: RootState): string | Date =>
  state.employeeList.country
const searchInput = (state: RootState): string => state.employeeList.searchInput

const employeeListThunk = {
  getEmployees,
  updateReportee,
}

const employeeListSelectors = {
  isLoading,
  employees,
  listSize,
  selectedEmploymentStatus,
  getSelectedStatusValue,
  isUpdated,
  SelectCustom,
  FromDateFilter,
  ToDateFilter,
  selectedCountry,
  searchInput,
}

export const employeeListService = {
  ...employeeListThunk,
  actions: employeeListSlice.actions,
  selectors: employeeListSelectors,
}

export default employeeListSlice.reducer
